/**
 * Small, dependency-free music theory helpers (12-TET, MIDI note numbers).
 */

export const PITCH_CLASS_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

export const SCALES = {
  minor: [0, 2, 3, 5, 7, 8, 10],
  major: [0, 2, 4, 5, 7, 9, 11],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
} as const satisfies Record<string, readonly number[]>;

export type ScaleName = keyof typeof SCALES;
export const SCALE_NAMES = Object.keys(SCALES) as ScaleName[];

export const SCALE_LABELS: Record<ScaleName, string> = {
  minor: 'minor',
  major: 'major',
  phrygian: 'phrygian',
  dorian: 'dorian',
  harmonicMinor: 'harmonic minor',
};

const LETTER_PC: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

/** Parses a pitch-class name like "F", "f#", "Bb", "c sharp" → 0–11, or undefined. */
export function parsePitchClass(input: string): number | undefined {
  const m = input.trim().toLowerCase().match(/^([a-g])\s*(#|♯|sharp|b|♭|flat)?$/);
  if (!m) return undefined;
  const base = LETTER_PC[m[1]!]!;
  const acc = m[2];
  const shift = acc === '#' || acc === '♯' || acc === 'sharp' ? 1 : acc === 'b' || acc === '♭' || acc === 'flat' ? -1 : 0;
  return (base + shift + 12) % 12;
}

/** Parses scientific pitch notation ("E1", "F#2", "Bb0") into a MIDI number (C4 = 60). */
export function noteNameToMidi(name: string): number | undefined {
  const m = name.match(/^([A-Ga-g])(#|b)?(-?\d)$/);
  if (!m) return undefined;
  const pc = parsePitchClass(`${m[1]}${m[2] ?? ''}`)!;
  return (Number(m[3]) + 1) * 12 + pc;
}

export function midiToNoteName(midi: number): string {
  return `${PITCH_CLASS_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
}

export function midiToFrequency(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

export function keyLabel(root: number, scale: ScaleName): string {
  return `${PITCH_CLASS_NAMES[root]} ${SCALE_LABELS[scale]}`;
}

/**
 * MIDI note for a scale degree (0-based, may be negative or ≥7 to cross octaves)
 * relative to `root` placed in `baseOctave` (MIDI octave numbering, C4 = 60).
 */
export function degreeToMidi(root: number, scale: ScaleName, degree: number, baseOctave: number): number {
  const steps = SCALES[scale];
  const octaveShift = Math.floor(degree / 7);
  const index = ((degree % 7) + 7) % 7;
  return (baseOctave + 1) * 12 + root + steps[index]! + 12 * octaveShift;
}

/** Triad/seventh chord tones (MIDI) built in-scale on a degree. */
export function chordTones(root: number, scale: ScaleName, degree: number, baseOctave: number, size: 3 | 4 = 3): number[] {
  return Array.from({ length: size }, (_, i) => degreeToMidi(root, scale, degree + i * 2, baseOctave));
}

/** True when a MIDI note's pitch class belongs to the key. */
export function inScale(midi: number, root: number, scale: ScaleName): boolean {
  const pc = (((midi - root) % 12) + 12) % 12;
  return (SCALES[scale] as readonly number[]).includes(pc);
}

/** All in-scale MIDI notes in [low, high], ascending. */
export function scaleNotesInRange(root: number, scale: ScaleName, low: number, high: number): number[] {
  const out: number[] = [];
  for (let m = low; m <= high; m++) if (inScale(m, root, scale)) out.push(m);
  return out;
}
